import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Container from '../components/common/Container'
import { useDispatch } from 'react-redux';
// import { RootState } from '../redux/store';
// import { setSelectedIndex } from '../redux/personalVerificationSlice';
import { useTranslation } from 'react-i18next';
import InputSearchBox from '../components/common/InputSearchBox';
import DateSearchBox from '../components/common/DateSearchBox';
import SelectSearchBox from '../components/common/SelectSearchBox';
import { PersonalVerificationMethod, PersonalVerificationDocument, Status, PersonalVerificationData } from '../types/personalVerification';
import Button from '../components/common/Button';
import PersonalVerifictionDetailModal from '../components/modal/PersonalVerificationDetailModal';
import MemoModal from '../components/modal/MemoModal';
import MemoButton from '../components/common/MemoButton';
import { GET_PERSONAL_VERIFICATION } from '../graphql/queries';
import { SAVE_VERIFICATION_MEMO } from '../graphql/mutations';
import { addOneDay, formatDateTime } from '../utils/datetime';
import Loading from '../components/common/Loading';

type SortField = 'id' | 'personalVerificationMethod' | 'personalVerificationDocument' | 'receivedDate' | 'verificationDate' | 'status' | 'memo';
type SortDirection = 'asc' | 'desc';

const PersonalVerification: React.FC = () => {
    // const personalVerificationInfo = useSelector((state: RootState) => state.personalVerification);
    // const dispatch = useDispatch();

    const { t } = useTranslation('verification');

    const [ verificationId, setVerificationId] = useState<string>('');
    const [ publicId, setPublicId] = useState<string>('');
    const [ receivedStartDate, setReceivedStartDate ] = useState<string>('');
    const [ receivedEndDate, setReceivedEndDate ] = useState<string>('');
    const [ verificationStartDate, setVerificationStartDate ] = useState<string>('');
    const [ verificationEndDate,setVerificationEndDate ] = useState<string>('');
    const [ personalVerificationMethod, setPersonalVerificationMethod ] = useState<PersonalVerificationMethod | 'all'>('all');
    const [ personalVerificationDocument, setPersonalVerificationDocument ] = useState<PersonalVerificationDocument | 'all'>('all');
    const [ status, setStatus ] = useState<Status | 'all'>('all');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [totalPage, setTotalPage] = useState<number>(0);
    const [selectedPage, setSelectedPage] = useState<number>(1);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [sortField, setSortField] = useState<SortField>('receivedDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const { loading, error, data, refetch } = useQuery(GET_PERSONAL_VERIFICATION, {
        variables: {
            id: null,
            verification_id: null,
            receivedStartDate: null,
            receivedEndDate: null,
            verificationStartDate: null,
            verificationEndDate: null,
            personalVerificationMethod: null,
            personalVerificationDocument: null,
            status: null,
        }
    });


    const sortedData = useMemo(() => {
        if (!data || !data.allPersonalVerification) return [];
        
        const sorted = [...data.allPersonalVerification].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'id':
                    aValue = a.id || '';
                    bValue = b.id || '';
                    break;
                case 'personalVerificationMethod':
                    aValue = a.personalVerificationMethod || '';
                    bValue = b.personalVerificationMethod || '';
                    break;
                case 'personalVerificationDocument':
                    aValue = a.personalVerificationDocument || '';
                    bValue = b.personalVerificationDocument || '';
                    break;
                case 'receivedDate':
                    aValue = new Date(a.receivedDate || 0);
                    bValue = new Date(b.receivedDate || 0);
                    break;
                case 'verificationDate':
                    aValue = new Date(a.verificationDate || 0);
                    bValue = new Date(b.verificationDate || 0);
                    break;
                case 'status':
                    aValue = a.state === "verified" ? a.result : a.state;
                    bValue = b.state === "verified" ? b.result : b.state;
                    break;
                case 'memo':
                    aValue = a.verificationMemo || '';
                    bValue = b.verificationMemo || '';
                    break;
                default:
                    return 0;
            }
            
            if (aValue instanceof Date && bValue instanceof Date) {
                return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
            
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
        
        return sorted;
    }, [data, sortField, sortDirection]);

    useEffect(() => {
        if(sortedData) {
            setTotalPage(Math.max(1, Math.ceil(sortedData.length / 10)));
        }
    }, [sortedData]);

    const [saveVerificationMemo] = useMutation(SAVE_VERIFICATION_MEMO, {
        onCompleted: () => {
            console.log('Legal person reference created successfully');
            window.location.reload();
        },
        onError: (error) => {
            console.error('Error creating legal person reference:', error);
            alert(t('error creating legal person reference'));
        }
    });

    const isValidPersonalVerificationMethod = useCallback((value: string): value is PersonalVerificationMethod | 'all' => {
        return Object.values(PersonalVerificationMethod).includes(value as PersonalVerificationMethod) || value === 'all';
    }, [])

    const personalVerificationMethodOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(PersonalVerificationMethod).map(method => ({
            value: method,
            label: t(method)
        }))
    ], []);

    const isValidPersonalVerificationDocument = useCallback((value: string): value is PersonalVerificationDocument | 'all' => {
        return Object.values(PersonalVerificationDocument).includes(value as PersonalVerificationDocument) || value === 'all';
    }, []);

    const personalVerificationDocumentOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(PersonalVerificationDocument).map(method => ({
            value: method,
            label: t(method)
        }))
    ], []);

    const isValidStatus = useCallback((value: string): value is Status | 'all' => {
        return Object.values(Status).includes(value as Status) || value === 'all';
    }, [])

    const statusOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(Status).map(method => ({
            value: method,
            label: t(method)
        }))
    ], []);

    const handleCloseDetailModal = useCallback(() => setIsDetailModalOpen(false), []);
    const handleCloseMemoModal = useCallback(() => setIsMemoModalOpen(false), []);
    const handleChangeVerificationId = useCallback((value: string) => setVerificationId(value), []);
    const handleChangePublicId = useCallback((value: string) => setPublicId(value), []);
    const handleChangeReceivedStartDate = useCallback((value: string) => setReceivedStartDate(value), []);
    const handleChangeReceviedEndDate = useCallback((value: string) => setReceivedEndDate(value), []);
    const handleChangeVerificationStartDate = useCallback((value: string) => setVerificationStartDate(value), []);
    const handleChangeVerificationEndDate = useCallback((value: string) => setVerificationEndDate(value), []);
    const handleChangePersonalVerificationMethodOptions = useCallback((value: string) => {
        if (isValidPersonalVerificationMethod(value)) {
            setPersonalVerificationMethod(value);
        } else {
            console.error('Invalid PersonalVerificationMethod:', value);
        }
    }, []);
    const handleUpdateMemo = (value: string) => {
        saveVerificationMemo({
            variables: {
                verificationMemo: value,
                verificationPublicId: data.allPersonalVerification[selectedIndex].publicId,
            }
        })
        .then(
            () => setIsMemoModalOpen(false)
        ).catch(
            err => console.log(err)
        )
    }
    const handleChangePersonalVerificaitonDocumentOptions = useCallback((value: string) => {
        if (isValidPersonalVerificationDocument(value)) {
            setPersonalVerificationDocument(value);
        } else {
            console.error('Invalid PersonalVerificationDocument:', value);
        }
    }, []);
    const handleChangeStatusOptions = useCallback((value: string) => {
        if (isValidStatus(value)) {
            setStatus(value);
        } else {
            console.error('Invalid Status:', value);
        }
    }, []);

    const handleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setSelectedPage(1);
    }, [sortField, sortDirection]);

    const getSortIcon = useCallback((field: SortField) => {
        if (sortField !== field) return ' ↕️';
        return sortDirection === 'asc' ? ' 🔼' : ' 🔽';
    }, [sortField, sortDirection]);

    const onHandlePrevPage = () => setSelectedPage(prev => prev - 1 < 1 ? 1 : prev - 1);
    const onHandleNextPage = () => setSelectedPage(prev => prev + 1 > totalPage ? prev : prev + 1);
    console.log(selectedPage);
    if (loading) return <Loading />;
    if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

    return (
        <Container label={t('personal verification history')}>
            <PersonalVerifictionDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                verificationDetailData={sortedData[selectedIndex]}
            />
            <MemoModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                onConfirm={handleUpdateMemo}
                memoData={sortedData[selectedIndex].verificationMemo}
                companyName={null}
            />
            <div className="search-form">
                <InputSearchBox
                    inputValue={publicId}
                    placeholder={t('search by ID or Public Id')}
                    onChange={handleChangePublicId}
                />
                <DateSearchBox
                    label={t('received date start date')}
                    inputValue={receivedStartDate}
                    onChange={handleChangeReceivedStartDate}    
                />
                <DateSearchBox
                    label={t('end date')}
                    inputValue={receivedEndDate}
                    onChange={handleChangeReceviedEndDate}
                />
                <DateSearchBox
                    label={t('verification date start date')}
                    inputValue={verificationStartDate}
                    onChange={handleChangeVerificationStartDate}
                />
                <DateSearchBox
                    label={t('end date')}
                    inputValue={verificationEndDate}
                    onChange={handleChangeVerificationEndDate}
                />
                <SelectSearchBox
                    label={t('personal verification method')}
                    options={personalVerificationMethodOptions}
                    selectedValue={personalVerificationMethod}
                    onChange={handleChangePersonalVerificationMethodOptions}
                />
                <SelectSearchBox
                    label={t('personal verification document')}
                    options={personalVerificationDocumentOptions}
                    selectedValue={personalVerificationDocument}
                    onChange={handleChangePersonalVerificaitonDocumentOptions}
                />
                <SelectSearchBox
                    label={t('status')}
                    options={statusOptions}
                    selectedValue={status}
                    onChange={handleChangeStatusOptions}
                />
                <div className="ml-20 inline-block">
                    <Button
                        label={t('search')}
                        onClick={() => {
                            refetch({
                                id: publicId || null,
                                receivedStartDate: receivedStartDate || null,
                                receivedEndDate: receivedEndDate,
                                verificationStartDate: verificationStartDate || null,
                                verificationEndDate: verificationEndDate,
                                personalVerificationMethod: personalVerificationMethod === "all" ? null : personalVerificationMethod,
                                personalVerificationDocument: personalVerificationDocument === "all" ? null : personalVerificationDocument,
                                status: status === "all" ? null : status,
                            });
                            setSelectedPage(1);
                        }}
                    />
                </div>
                <div className="pl-20 inline-block cursor-pointer"
                    onClick={() => {
                        console.log('Resetting search criteria');
                        setVerificationId('');
                        setPublicId('');
                        setReceivedStartDate('');
                        setReceivedEndDate('');
                        setVerificationStartDate('');
                        setVerificationEndDate('');
                        setPersonalVerificationMethod('all');
                        setPersonalVerificationDocument('all');
                        setStatus('all');
                        refetch({
                            id: null,
                            verification_id: null,
                            receivedStartDate: null,
                            receivedEndDate: null,
                            verificationStartDate: null,
                            verificationEndDate: null,
                            personalVerificationMethod: null,
                            personalVerificationDocument: null,
                            status: null,
                        });
                        setSelectedPage(1)
                        window.location.reload();
                    }}>
                    <span className="underline">{t('reset')}</span>
                </div>
            </div>
            <table>
                <thead>
                    <tr>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('id')}
                        >
                            {t('id')}{getSortIcon('id')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('personalVerificationMethod')}
                        >
                            {t('personal verification method')}{getSortIcon('personalVerificationMethod')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('personalVerificationDocument')}
                        >
                            {t('personal verification document')}{getSortIcon('personalVerificationDocument')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('receivedDate')}
                        >
                            {t('received datetime')}{getSortIcon('receivedDate')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('verificationDate')}
                        >
                            {t('verified datetime')}{getSortIcon('verificationDate')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('status')}
                        >
                            {t('status')}{getSortIcon('status')}
                        </th>
                        <th 
                            className="cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('memo')}
                        >
                            メモ{getSortIcon('memo')}
                        </th>
                    </tr>
                </thead>
                {sortedData.filter((_: any, index: number) => (index >= (selectedPage - 1) * 10) && (index < selectedPage  * 10)).map((value: any, index: number) => {
                    const getVerificationIdStyle = (isDeletedFromTrustdock: boolean, lastDownloadedDate: string | null) => {
                        console.log(isDeletedFromTrustdock, lastDownloadedDate);
                        if (isDeletedFromTrustdock) {
                            return 'line-through double'; // 打ち消し線
                        }
                        if (!isDeletedFromTrustdock && lastDownloadedDate !== null) {
                            return 'line-through dotted'; // 打ち消し線
                        }
                        return '';
                    };

                    return (
                        <tr className="group">
                            <td style={{textDecoration: getVerificationIdStyle(value.isDeletedFromTrustdock, value.lastDownloadedDate)}}>
                                {value.id}
                            </td>
                            <td>{t(value.personalVerificationMethod)}</td>
                            <td>{t(value.personalVerificationDocument)}</td>
                            <td>{formatDateTime(value.receivedDate)}</td>
                            <td>{formatDateTime(value.verificationDate)}</td>
                            <td
                                className={value.result === "approved" ? 'status-completed' : (value.result === "denied" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                   }}
                                >
                                    {value.state === "verified" ? t(value.result) : t(value.state)}
                                </a>
                            </td>
                            <td>
                                <MemoButton
                                    hasMemo={!!value.verificationMemo}
                                    onClick={() => {
                                        setSelectedIndex((selectedPage - 1) * 10 + index);
                                        setIsMemoModalOpen(true);
                                    }}
                                />
                            </td>
                        </tr>
                    )
                })}
            </table>
            <div className="pagination">
                <Button
                    label={t('prev page')}
                    onClick={onHandlePrevPage}
                    addedClass='mx-[10px]'
                />
                <span>{selectedPage}</span>
                &nbsp;/&nbsp;
                <span>{totalPage}</span>
                <Button
                    label={t('next page')}
                    onClick={onHandleNextPage}
                    addedClass='mx-[10px]'
                />
            </div>
        </Container>
    );
};

export default PersonalVerification;
